import {
  ThreeToolsInstance,
  usePartDefinition,
  useProductStoreContext,
} from "@project/emerge";
import { markProductSceneChanged } from "@project/emerge/src/new/sceneActivity";
import type { AbstractPartDefinition } from "@project/emerge-types";
import { calculateRivetPositions } from "@project/pricing";
import { useEffect, useLayoutEffect, useMemo } from "react";
import {
  InstancedMesh,
  Matrix4,
  Quaternion,
  SphereGeometry,
  Vector3,
} from "three";
import type { LTPartType } from "../enum/PartType";
import { getRawChromeMaterial } from "../materials/ChromeMaterial";
import type { AngleSectionDefinition } from "../parts/AngleSectionDefinition";
import {
  createAngleSectionEdges,
  createAngleSectionLeg,
} from "./angleSectionResources";
import { PART_EDGE_THRESHOLD_DEG } from "./PartEdges";
import { useLatestCommittedResource } from "./useLatestCommittedResource";

/** Chamfer on the angle-section edges, in millimetres. */
const ANGLE_SECTION_BEVEL = 0.25;

// Definiere einen passenden Typ für den Renderer
interface AngleSectionRendererProps
  extends AbstractPartDefinition<LTPartType.AngleSection> {
  length: number;
  partSelection: AngleSectionDefinition["partSelection"];
  cuts?: AngleSectionDefinition["cuts"];
}

export const AngleSectionRenderer = ({ id }: { id: string }) => {
  const useProductStore = useProductStoreContext();
  const showEdges = useProductStore((state) => state.showEdges);

  // Nur die Eigenschaften abonnieren, die für das Rendering benötigt werden
  const partDef = usePartDefinition<AngleSectionRendererProps>({
    id,
    select: ["length", "partSelection", "cuts"] as const,
  });

  // Extract values with defaults (hooks must be called before early return)
  const length = partDef?.length ?? 0;
  const partSelection = partDef?.partSelection;
  const cuts = partDef?.cuts;

  // Get the dimensions from the partSelection
  const strength = partSelection?.strength || 0.2;
  const depth = partSelection?.depth || 1;
  const cutSize = partSelection?.cutSize || 0;

  // Create the horizontal rectangle shape (XZ plane) with optional cut corners
  const horizontalRectShape = useMemo(() => {
    // Define the horizontal rectangle points (in XZ plane)
    const points: Array<[number, number]> = [];

    // Start at top left
    points.push([-length / 2, depth]);

    // Bottom left - with optional cut
    if (cuts?.right?.bottom && cutSize > 0) {
      // Add a point for the cut
      points.push([-length / 2, cutSize], [-length / 2 + cutSize, 0]);
    } else {
      points.push([-length / 2, 0]);
    }

    // Top right - with optional cut
    if (cuts?.left?.bottom && cutSize > 0) {
      // Add a point for the cut
      points.push([length / 2 - cutSize, 0], [length / 2, cutSize]);
    } else {
      points.push([length / 2, 0]);
    }

    // Bottom left and close the shape
    points.push([length / 2, depth], [-length / 2, depth]);

    // Create a Shape from the points
    return ThreeToolsInstance.pointsToShape(points);
  }, [length, depth, cuts, cutSize]);

  // Create the vertical rectangle shape (XY plane) with optional cut corners
  const verticalRectShape = useMemo(() => {
    // Define the vertical rectangle points (in XY plane)
    const points: Array<[number, number]> = [];

    // Start at bottom left, then bottom right
    points.push([-length / 2, 0], [length / 2, 0]);

    // Top right - with optional cut
    if (cuts?.left?.top && cutSize > 0) {
      // Add a point for the cut
      points.push([length / 2, depth - cutSize], [length / 2 - cutSize, depth]);
    } else {
      points.push([length / 2, depth]);
    }

    // Top left - with optional cut
    if (cuts?.right?.top && cutSize > 0) {
      // Add a point for the cut
      points.push(
        [-length / 2 + cutSize, depth],
        [-length / 2, depth - cutSize],
      );
    } else {
      points.push([-length / 2, depth]);
    }

    // Close the shape
    points.push([-length / 2, 0]);

    // Create a Shape from the points
    return ThreeToolsInstance.pointsToShape(points);
  }, [length, depth, cuts, cutSize]);

  // Create the extrusion settings.
  // The chamfer is pulled inwards (`bevelOffset`) and taken out of the depth,
  // so the section keeps its exact thickness — it only softens the edge enough
  // to catch a highlight. Sections too thin to carry one stay square.
  const extrudeSettings = useMemo(() => {
    const bevel = strength > ANGLE_SECTION_BEVEL * 4 ? ANGLE_SECTION_BEVEL : 0;
    return {
      steps: 1,
      depth: strength - bevel * 2,
      bevelEnabled: bevel > 0,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelOffset: -bevel,
      bevelSegments: 1,
      bevel,
    };
  }, [strength]);

  const legKey = partDef
    ? JSON.stringify([
        length,
        depth,
        strength,
        cutSize,
        !!cuts?.right?.bottom,
        !!cuts?.left?.bottom,
        !!cuts?.left?.top,
        !!cuts?.right?.top,
      ])
    : null;
  const legs = useLatestCommittedResource(legKey, () => {
    const horizontal = createAngleSectionLeg(
      horizontalRectShape,
      extrudeSettings,
      depth,
      strength,
      true,
    );
    try {
      const vertical = createAngleSectionLeg(
        verticalRectShape,
        extrudeSettings,
        depth,
        strength,
        false,
      );
      return {
        value: { horizontal: horizontal.value, vertical: vertical.value },
        release: () => {
          horizontal.release();
          vertical.release();
        },
      };
    } catch (error) {
      horizontal.release();
      throw error;
    }
  });

  // Edge toggles own only edge resources; do not rebuild the extrusions/maps.
  useLayoutEffect(() => {
    if (!legs) return;
    const edges = showEdges
      ? [legs.horizontal, legs.vertical].map((mesh) =>
          createAngleSectionEdges(mesh, PART_EDGE_THRESHOLD_DEG),
        )
      : [];
    markProductSceneChanged(legs.horizontal);
    return () => {
      for (const edge of edges) edge.release();
    };
  }, [legs, showEdges]);

  // Early return after all hooks
  if (!partDef || !legs) return null;

  return (
    // Carries the base orientation. The rotation point is already the inner
    // corner of the L in local coordinates, so no offset group is needed
    // around this one.
    <group rotation={[Math.PI, 0, 0]}>
      {/* Horizontal part */}
      <primitive object={legs.horizontal} />

      {/* Vertical part */}
      <primitive object={legs.vertical} />

      <Rivets length={length} depth={depth} strength={strength} />
    </group>
  );
};

/**
 * One geometry for every rivet on every angle section.
 *
 * It used to be an 8x8 sphere built per rivet pair, which read as a faceted
 * blob next to the imported hardware — those parts carry baked normal maps and
 * are smooth, so the two kinds of rivet in one picture looked like two
 * different resolutions. Enough segments around the dome to hold a highlight,
 * few enough up its height that a part with a few hundred rivets stays cheap:
 * a case can carry hundreds, each one drawn once per scene pass, and there are
 * three passes per frame (ground shadow, colour, AO G-buffer).
 */
const RIVET_GEOMETRY = new SphereGeometry(4, 16, 8);

const IDENTITY_ROTATION = new Quaternion();

/**
 * All rivets of one angle section, as two instanced draws.
 *
 * Each rivet used to be its own mesh with its own material: a case with a few
 * hundred of them cost a few hundred draw calls and as many material
 * instances — and that is per scene pass, of which there are three (ground
 * shadow, colour, AO G-buffer). Instancing makes it two draws per section
 * regardless of how many rivets it carries.
 */
const Rivets = ({
  length,
  depth,
  strength,
}: {
  length: number;
  depth: number;
  strength: number;
}) => {
  // Chrome, not the sheet metal this used to share with the corner parts:
  // rivets are plated steel, and giving them their own material is what lets
  // them read as fasteners rather than as bumps in the extrusion.
  const material = useMemo(() => getRawChromeMaterial(), []);

  // Derived here rather than passed in: `calculateRivetPositions` returns a
  // fresh array on every call, so an array prop would change identity on every
  // parent render and rebuild both instanced meshes for an identical layout.
  const positions = useMemo(() => calculateRivetPositions(length), [length]);

  const [horizontal, vertical] = useMemo(() => {
    // A section too short to carry a rivet still renders; building the two
    // meshes for it would allocate an instance buffer nothing ever draws.
    if (positions.length === 0) return [null, null];
    const matrix = new Matrix4();
    const build = (place: (x: number) => [Vector3, Vector3]) => {
      const mesh = new InstancedMesh(
        RIVET_GEOMETRY,
        material,
        positions.length,
      );
      positions.forEach((x, index) => {
        const [position, scale] = place(x);
        matrix.compose(position, IDENTITY_ROTATION, scale);
        mesh.setMatrixAt(index, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      return mesh;
    };

    return [
      build((x) => [
        new Vector3(x, -strength, depth / 2),
        new Vector3(1, 0.3, 1),
      ]),
      build((x) => [
        new Vector3(x, depth / 2, -strength),
        new Vector3(1, 1, 0.3),
      ]),
    ];
  }, [positions, depth, strength, material]);

  // The meshes and the material have different lifetimes: the meshes are
  // rebuilt whenever the layout changes, the material outlives them. Disposing
  // both from one effect would free the material the *new* meshes are already
  // using, forcing a shader recompile on every rebuild.
  useEffect(() => {
    return () => {
      horizontal?.dispose();
      vertical?.dispose();
    };
  }, [horizontal, vertical]);

  useEffect(() => () => material.dispose(), [material]);

  if (!horizontal || !vertical) return null;

  return (
    <>
      <primitive object={horizontal} />
      <primitive object={vertical} />
    </>
  );
};
